<template>
  <div>
    <!-- Date Range Filters -->
    <Popover ref="dateRangePopover" :showHeader="true" headerTitle="Date Range" sizeClass="popover-xlarge">
      <div class="filter-content grid-force-newlines">
        <div class="row mb-4" v-if="dateFilterColumnList.length > 0">
          <div class="col-12">
            <ButtonGroup :buttons="dateFilterButtonGroupList" v-model="editingDateRangeFilter.dateFilterIndex" />
          </div>
        </div>
        <ReportDateRangeSelect
          :startDateLabel="dateStartFilterLabel"
          :endDateLabel="dateEndFilterLabel"
          :startDate="editingDateRangeFilter.startDate"
          :endDate="editingDateRangeFilter.endDate"
          @updateStartDate="editingDateRangeFilter.startDate = $event"
          @updateEndDate="editingDateRangeFilter.endDate = $event"
        />
      </div>
      <div class="filter-content-footer row">
        <div class="col-4">
          <FormButton class="btn-block btn-primary btn-md" @click="applyDateRangeFilter"><i class="far fa-check-circle"></i> Apply</FormButton>
        </div>
        <div class="col-4"> </div>
        <div class="col-4 text-right">
          <FormButton class="btn-block btn-warning btn-md" @click="resetDateRangeFilter"><i class="far fa-times"></i> Reset</FormButton>
        </div>
      </div>
    </Popover>

    <div class="row m-0">
      <div class="col-sm-12">
        <div v-if="displayTools" class="float-right ml-4">
          <slot name="tools" />
        </div>
        <div class="dt-toolbar-item ml-2" v-if="displayActions">
          <div class="dt-toolbar-item dt-toolbar-entries-list" data-toggle="dropdown">
            <div class="input-group">
              <div class="input-group-prepend dt-filter-item"><span class="input-group-addon"><i class="far fa-tools"></i></span></div>
            </div>
          </div>
          <ul class="dropdown-menu dropdown-menu-right">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <slot name="actionList" />
          </ul>         
        </div>
        <div v-if="displayPageCount">
          <div class="dt-toolbar-item dt-toolbar-entries-list" data-toggle="dropdown">
            <div class="input-group">
              <div class="input-group-prepend"><span class="input-group-addon"><i class="far fa-list"></i></span></div>
              <div class="form-control dt-entries-text">Showing {{pageLengthCountDisplay}} Entries</div>
            </div>
          </div>
          <ul class="dropdown-menu dropdown-menu-right">
            <DropdownMenuLabel>Page Count</DropdownMenuLabel>
            <li><a href="#" @click.prevent="setPageLength(25)" :class="['dropdown-menu-item-icon', this.$data.dtPageLength==25 ? 'font-weight-bold' : '']"><i :class="['far', this.$data.dtPageLength==25 ? 'fa-check-circle' : 'fa-circle']"></i> 25 Entries</a></li>
            <li><a href="#" @click.prevent="setPageLength(50)" :class="['dropdown-menu-item-icon', this.$data.dtPageLength==50 ? 'font-weight-bold' : '']"><i :class="['far', this.$data.dtPageLength==50 ? 'fa-check-circle' : 'fa-circle']"></i> 50 Entries</a></li>
            <li><a href="#" @click.prevent="setPageLength(100)" :class="['dropdown-menu-item-icon', this.$data.dtPageLength==100 ? 'font-weight-bold' : '']"><i :class="['far', this.$data.dtPageLength==100 ? 'fa-check-circle' : 'fa-circle']"></i> 100 Entries</a></li>
            <li><a href="#" @click.prevent="setPageLength(200)" :class="['dropdown-menu-item-icon', this.$data.dtPageLength==200 ? 'font-weight-bold' : '']"><i :class="['far', this.$data.dtPageLength==200 ? 'fa-check-circle' : 'fa-circle']"></i> 200 Entries</a></li>
            <li><a href="#" @click.prevent="setPageLength(-1)" :class="['dropdown-menu-item-icon', this.$data.dtPageLength==-1 ? 'font-weight-bold' : '']"><i :class="['far', this.$data.dtPageLength==-1 ? 'fa-check-circle' : 'fa-circle']"></i> Show All Entries</a></li>
          </ul>
        </div>
        <div class="mr-2 dt-toolbar-item" v-if="displayExportTools">
          <div class="dt-toolbar-item dt-toolbar-entries-list" data-toggle="dropdown">
            <div class="input-group">
              <div class="input-group-prepend dt-filter-item"><span class="input-group-addon"><i class="far fa-file-export"></i></span></div>
            </div>
          </div>
          <ul class="dropdown-menu dropdown-menu-right">
            <DropdownMenuLabel>Export Displayed Rows</DropdownMenuLabel>
            <li><a href="#" @click.prevent="exportData('csv')" class="dropdown-menu-item-icon"><i class="far fa-file-csv"></i> Export CSV</a></li>
            <li><a href="#" @click.prevent="exportData('pdf')" class="dropdown-menu-item-icon"><i class="far fa-file-pdf"></i> Export PDF</a></li>
          </ul>         
        </div>
        <div :class="['mr-2', 'dt-toolbar-item', { 'disabled': isFiltersDisabled }]" v-if="displayLocationFilter">
          <FilterInput
            ref="locationFilterInputButton"
            :filterOptions="locationFilterList"
            v-model="selectedLocationFilters"
            @update="locationFiltersUpdated()"
            title="Location Filter" 
            buttonIcon="fa-map-marker-alt"
          />
        </div>
        <div :class="['mr-2', 'dt-toolbar-item', { 'disabled': isFiltersDisabled }]" v-if="hasFilterOptions && displayFilters">
          <FilterInput ref="filterInputButton" :filterOptions="filterList" v-model="selectedFilters" @update="filtersUpdated()" />
        </div>
        <div :class="['mr-2', 'dt-toolbar-item', { 'disabled': isFiltersDisabled }]" v-if="hasDateRangeFilter && displayDateFilter">
          <div class="input-group">
            <div class="input-group-prepend dt-filter-item" @click="displayDatePopover">
              <span class="input-group-addon"><i :class="[appliedDateRangeFilter.startDate || appliedDateRangeFilter.endDate ? 'text-info' : '', appliedDateRangeFilter.startDate || appliedDateRangeFilter.endDate ? 'fas' : 'far', 'fa-calendar']"></i></span>
            </div>
            <div class="form-control" v-if="appliedDateRangeFilter.startDate || appliedDateRangeFilter.endDate">
              <span v-if="appliedDateRangeFilter.startDate" :class="['label', 'dt-filter-tag']" @click.prevent="removeDateRangeStart">{{dateFilterName}} After: {{appliedDateRangeFilter.startDate | formatDate}}</span>
              <span v-if="appliedDateRangeFilter.endDate" :class="['label', 'dt-filter-tag']" @click.prevent="removeDateRangeEnd">{{dateFilterName}} Before: {{appliedDateRangeFilter.endDate | formatDate}}</span>
            </div>
          </div>
        </div>
        <div class="dt-loading-panel" v-show="loadingData">
          <i class="far fa-spinner-third fa-spin"></i> Loading...
        </div>
        <div :class="['mr-2', 'dt-toolbar-item', { 'disabled': isFiltersDisabled }]" v-if="displaySearch">
          <div class="input-group">
            <div class="input-group-prepend"><span class="input-group-addon"><i class="far fa-search"></i></span></div>
            <input type="text" placeholder="Search" class="form-control" v-model="searchText">
          </div>
        </div>
        <div class="dt-toolbar-item mr-2" v-show="loadingError">
          <NotificationBar type="error">
            <NotificationBarTitle>Error: {{loadingError}}</NotificationBarTitle>
          </NotificationBar>
        </div>
        <slot name="title" />
      </div>
    </div>

    <div class="table-responsive dt-table-container">
      <div class="container-fluid">
        <div class="row">
          <div class="col-sm-12">
            <table class="table table-striped table-hover table-data-glass" style="width:100%">
              <thead>
                <slot name="tableHeader" />
                <tr>
                  <th
                    v-for="(header, index) in displayColumns"
                    :key="index"
                    :class="getColumnHeaderClass(index)"
                    @click="columnHeaderClicked(index)" :width="header.width ? header.width : '0'">
                    {{header.title}}
                    <slot name="tableHeaderCell" :data="index" />
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="row in rowList">
                  <slot name="row" :data="row" />
                </template>
                <template v-if="rowList.length == 0">
                  <tr>
                    <td :colspan="displayColumns.length" class="text-center font-italic">{{emptyListMessage}}</td>
                  </tr>
                </template>
              </tbody>
              <tfoot v-if="dtSummary">
                <slot name="summary" :data="dtSummary" />
              </tfoot>
            </table>
          </div>
        </div>

        <div class="row">
          <div class="col-sm-4 pt-2" v-show="displayFooterNote">
            Showing {{dtStartIndex+1}} to {{dtStartIndex+rowList.length}} of {{dtFilteredRecords}} entries
            <span v-if="dtFilteredRecords < dtTotalRecords">(filtered from {{dtTotalRecords}} total entries)</span>
          </div>
          <div class="col-sm-4 text-center" v-show="displayPageButtons">
            <PaginatedButtonGroup v-model="dtCurrentPage" @update="setPageIndex($event)" :pageCount="dtPageCount" />
          </div>
          <div class="col-sm-4">
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style lang="scss">
/* Styling */
.table-data-glass {
  border-spacing: 0px;
  /*border: 1px solid #efefef;*/
}
.dt-table-container {
  min-height: 200px;    /* Helps datatables with no/minimal data still show popups/popovers correctly */
}

.table-data-glass > thead .sorting,
.table-data-glass > thead .sorting_asc,
.table-data-glass > thead .sorting_desc {
  cursor: pointer;
  position: relative;
}
.table-data-glass > tbody > tr > td {
  border: 0;
  padding: 10px;
}
.table-data-nav-link {
  font-weight: 600;
}
.table-data-glass > tfoot > tr > td {
  font-weight: 600;
}

/* Sort Icons */
table.table-data-glass thead .sorting:after {
  font-family: "Font Awesome 5 Pro";
  opacity: 0.2;
  content: "\f0dc";
  font-weight: 900;
}
table.table-data-glass thead .sorting_asc:after {
  font-family: "Font Awesome 5 Pro";
  content: "\f0de";
  font-weight: 900;
}
table.table-data-glass thead .sorting_desc:after {
  font-family: "Font Awesome 5 Pro";
  content: "\f0dd";
  font-weight: 900;
}
table.table-data-glass thead .sorting:after,
table.table-data-glass thead .sorting_asc:after,
table.table-data-glass thead .sorting_desc:after {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: block;
}


/* Toolbar */
.dt-filter-toolbar {
  display: inline-block;
}
.dt-search-toolbar {
  display: inline-block;
}
.dt-toolbar-item {
  float: right;
}
.dt-toolbar-item.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.dt-filter-item {
  border-radius: $control-border-radius;
  cursor: pointer;
  height: 35px;
}
.dt-filter-tag {
  cursor: pointer;
  margin-right: 3px;
}
.data-table-filter-box {
  height: 35px;
  display: none;
}
.dt-toolbar-entries-list {
  cursor: pointer;
}
.dt-entries-text {
  font-size: 12px;
  line-height: 35px;
  padding-top: 0px;
  padding-bottom: 0px;
  height: 35px;
}

.dt-loading-panel {
  position: absolute;
  padding: 10px;
  width: 120px;
  left: 40%;
  right: 50%;
  transform:translate(-50%, 0%);
  border: 1px solid #ebebeb;
  border-radius: $layout-minor-border-radius;
  background-color: #fafafa;
  font-weight: bold;
  text-align: center;
  font-size: 14px;
  color: #909090;
  z-index: 10;
}
.dt-loading-error {
  color: #ff0000;
  padding-top: 8px;
  padding-right: 20px;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapState } from 'vuex'

import form from '@/mixins/form'
import Popover from '@/components/popover.vue'
import FilterInput from '@/components/filter/filterInput'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

export default {
  name: 'DataTable',
  mixins: [form],
  components: {
    Popover,
    FilterInput,
    ReportDateRangeSelect
  },
  props: {
    url: String,
    columns: Array,
    filterOptions: Array,
    locationFilterOptions: Array,
    exportTitle: String,
    useQueryData: Boolean,

    useReportingServer: {
      type: Boolean,
      default: false
    },

    defaultColumnOrderIndex: {
      type: Number,
      default: 0
    },
    defaultColumnOrderDir: {
      type: String,
      default: 'ASC'
    },
    defaultPageLength: {
      type: Number,
      default: 50
    },

    // Turn on to block filters while loading data
    // Used for very long queries (20s+) to prevent multiple reloads & issues with integrity of data
    blockFiltersWhileLoading: {
      type: Boolean,
      default: false
    },
    
    displayTools: Boolean,
    displayActions: Boolean,
    displaySearch: {
      type: Boolean,
      default: true
    },
    displayPageCount: {
      type: Boolean,
      default: true
    },
    displayFilters: {
      type: Boolean,
      default: true
    },
    displayDateFilter: {
      type: Boolean,
      default: true
    },
    displayLocationFilter: {
      type: Boolean,
      default: false
    },
    displayFooterNote: {
      type: Boolean,
      default: true
    },

    debounceTime: {
      type: Number,
      default: 200
    },
    emptyListMessage: {
      type: String,
      default: 'No data available'
    },

    hasDateRangeFilter: {
      type: Boolean,
      default: false,
    },

    displayExportTools: Boolean,

    // DEPRECATED BY dateFilter in column properties
    dateRangeField: String,
    dateRangeName: String
    
  },
  data() {
    return {
      dt: null,
      filterList: _.cloneDeep(this.filterOptions),
      selectedFilters: [],
      selectedLocationFilters: [],
      dtStartIndex: 0,
      dtPageLength: this.defaultPageLength || 50,
      dtTotalRecords: 0,
      dtFilteredRecords: 0,
      dtPageCount: 0,
      dtCurrentPage: 0,
      searchText: '',
      currentDraw: 1,
      recvDraw: 0,
      rowList: [],
      dtSummary: null,
      loadingData: false,
      loadingError: '',
      orderColumnIndex: this.$props.defaultColumnOrderIndex,
      orderColumnDir: this.$props.defaultColumnOrderDir,

      // Date Range. Default to nothing
      defaultDateRangeFilter: {    
        startDate: '',
        endDate: '',
        dateFilterIndex: 0,
      },
      editingDateRangeFilter: {},
      appliedDateRangeFilter: {},

      //filterDateRangeStart: '',
      //filterDateRangeEnd: '',
      //currentDateFilterIndex: 0
    }
  },
  watch: {
    url() {
      this.debouncedUpdate()
    },
    filterOptions() {
      this.$data.filterList = _.cloneDeep(this.$props.filterOptions)
    },
    searchText() {
      this.debouncedSearch()
    },
    columns() {
      this.debouncedUpdate()
    }
  },
  created() {
    this.debouncedSearch = _.debounce(this.updateSearchFilter, this.$props.debounceTime)
    this.debouncedUpdate = _.debounce(this.updateTableData, this.$props.debounceTime)
  },
  mounted() {
    this.editingDateRangeFilter = _.clone(this.defaultDateRangeFilter)
    this.appliedDateRangeFilter = _.clone(this.defaultDateRangeFilter)
    
    // Handle data in the query string to control pagination / searching / filtering
    if(this.useQueryData) {
      if(this.$route.query && this.$route.query.filterFields) {
        var filterFields = (this.$route.query.filterFields + '').split(',').map(field => field.trim())

        if(filterFields.length > 0) {
          var filters = []

          filterFields.forEach(filterField => {
            if(!this.$route.query[filterField])
              return

            var values = (this.$route.query[filterField] + '')
            var valueList = values.split(',').map(value => value.trim())
            valueList.forEach(value => {
              filters.push({ value: value, field: filterField })
            })
          })

          this.applySelectedFilters(filters)
        }
      }
    }

    this.debouncedUpdate()
  },
  computed: {
    ...mapState({
      accountLocations: state => state.account.accountLocations
    }),
    hasFilterOptions() {
      return this.filterOptions != null
    },
    hasSelectedFilters() {
      return this.selectedFilters.length > 0
    },
    pageLengthCountDisplay() {
      if(this.$data.dtPageLength >= 0)
        return this.$data.dtPageLength
      return 'All'
    },
    displayPageButtons() {
      if(this.$data.dtPageLength == -1)
        return false
      if(Math.ceil(this.dtTotalRecords / this.$data.dtPageLength) <= 1)
        return false
      return true
    },
    displayColumns() {
      if(!this.columns)
        return []
      
      return this.columns.filter(column => !column.hidden)
    },
    dateFilterColumnList() {
      if(!this.columns)
        return []

      return this.columns.filter(column => column.dateFilter)
    },
    dateFilterButtonGroupList() {
      return this.dateFilterColumnList.map(column => {
        return {
          title: column.title
        }
      })
    },
    dateFilterName() {
      if(this.dateFilterColumnList.length == 0 || this.appliedDateRangeFilter.dateFilterIndex === undefined)
        return ''
      return this.dateFilterColumnList[this.appliedDateRangeFilter.dateFilterIndex].title
    },
    dateStartFilterLabel() {
      if(this.dateFilterColumnList.length == 0 || this.editingDateRangeFilter.dateFilterIndex === undefined)
        return 'Start Date'

      let title = this.dateFilterColumnList[this.editingDateRangeFilter.dateFilterIndex].title
      return `${title} After`
    },
    dateEndFilterLabel() {
      if(this.dateFilterColumnList.length == 0 || this.editingDateRangeFilter.dateFilterIndex === undefined)
        return 'End Date'

      let title = this.dateFilterColumnList[this.editingDateRangeFilter.dateFilterIndex].title
      return `${title} Before`
    },
    isFiltersDisabled() {
      return this.blockFiltersWhileLoading && this.loadingData
    },
    locationFilterList() {
      // Use custom 'locationFilterOptions' if provided, otherwise use default accountLocationId
      if(this.locationFilterOptions && this.locationFilterOptions.length > 0) {
        return this.locationFilterOptions
      }

      var locationOptions = (this.accountLocations || []).map(location => {
        return {
          name: location.name,
          value: location.id,
          colour: location.highlightColour || '#000000',
        }
      })
      return [{
        field: 'accountLocationId',
        name: 'Location',
        columnIndex: 0,
        options: locationOptions
      }]
    }
  },
  methods: {
    reload() {
      this.updateTableData()
    },
    getColumnHeaderClass(index) {
      var classList = []
      if(this.columns[index].class)
        classList.push(this.columns[index].class)

      if(this.columns[index].sortable !== undefined && this.columns[index].sortable == false)
        return classList
      
      if(index != this.orderColumnIndex)
        classList.push('sorting')
      else if(this.orderColumnDir == 'ASC')
        classList.push('sorting_asc')
      else
        classList.push('sorting_desc')

      return classList
    },
    columnHeaderClicked(index) {
      if(this.columns[index].sortable !== undefined && this.columns[index].sortable == false)
        return

      if(index == this.orderColumnIndex) {
        if(this.orderColumnDir == 'ASC')
          this.orderColumnDir = 'DESC'
        else
          this.orderColumnDir = 'ASC'
      } else {
        this.orderColumnIndex = index
        this.orderColumnDir = 'ASC'
      }

      this.updateTableData()
    },
    displayDatePopover(evt) {
      this.$refs.dateRangePopover.displayFromElement(evt.target)
    },
    removeDateRangeStart() {
      this.appliedDateRangeFilter.startDate = ''
      this.editingDateRangeFilter.startDate = ''
      this.updateTableData()
    },
    removeDateRangeEnd() {
      this.appliedDateRangeFilter.endDate = ''
      this.editingDateRangeFilter.endDate = ''
      this.updateTableData()
    },
    applyDateRangeFilter() {
      this.appliedDateRangeFilter = _.clone(this.editingDateRangeFilter)

      this.$refs.dateRangePopover.hidePopover()
      this.debouncedUpdate()
    },
    resetDateRangeFilter() {
      this.editingDateRangeFilter = _.clone(this.defaultDateRangeFilter)
      this.appliedDateRangeFilter = _.clone(this.defaultDateRangeFilter)

      this.$refs.dateRangePopover.hidePopover()
      this.debouncedUpdate()
    },
    setPageLength(pageLength) {
      this.$data.dtPageLength = pageLength
      this.resetCurrentPage()
      this.updateTableData()
    },
    setPageIndex(index) {
      if(index < 0 || index >= this.dtPageCount)
        return

      this.dtStartIndex = index * this.dtPageLength
      this.updateTableData()
    },
    resetCurrentPage() {
      this.dtStartIndex = 0
    },
    resetTableInfo() {
      this.searchText = ''
      this.resetDateRangeFilter()
      this.resetCurrentPage()
      this.resetOrderInfo()
      this.debouncedUpdate()
    },
    resetOrderInfo() {
      this.orderColumnIndex = this.$props.defaultColumnOrderIndex
      this.orderColumnDir = this.$props.defaultColumnOrderDir
    },
    updateSearchFilter() {
      this.resetCurrentPage()
      this.updateTableData()
    },
    filtersUpdated() {
      this.resetCurrentPage()
      this.debouncedUpdate()
    },
    locationFiltersUpdated() {
      this.resetCurrentPage()
      this.debouncedUpdate()
    },
    addSelectedFilters(filters) {
      this.selectedFilters = _.concat(this.selectedFilters, (filters || []).map(filter => {
        return { value: filter.value, field: filter.field }
      }))

      this.updateTableData()
    },
    applySelectedFilters(filters) {
      this.selectedFilters = (filters || []).map(filter => {
        return { value: filter.value, field: filter.field }
      })

      this.updateTableData()
    },
    removeSelectedFilters(filters) {
      this.selectedFilters = this.selectedFilters.filter(selected => {
        return filters.find(filter => filter.value == selected.value && filter.field == selected.field) == null
      })

      this.updateTableData()
    },
    updateTableData() {
      if(this.$props.url == null || this.$props.url == '')
        return

      this.loadingData = true
      this.loadingError = ''

      var draw = this.currentDraw++
      var apiURL = this.useReportingServer ? this.$glass.reportingApiUrl : this.$glass.apiUrl
      var url = apiURL + this.generateDataURL() + `&draw=${draw}`

      // If blocking, hide popovers while loading
      if(this.blockFiltersWhileLoading) {
        this.$refs.filterInputButton.hideFilterPopover()
      }

      this.$api.get(url).then(response => {
        // Discard results if the data is older than newer data we have
        if(response.data.draw >= this.recvDraw) {
          this.rowList = response.data.data
          this.dtTotalRecords = response.data.recordsTotal
          this.dtFilteredRecords = response.data.recordsFiltered
          this.dtSummary = response.data.summary

          this.dtPageCount = Math.ceil(this.dtFilteredRecords / this.$data.dtPageLength)

          this.dtCurrentPage = this.dtStartIndex / this.$data.dtPageLength
          this.dtCurrentPage = Math.min(this.dtCurrentPage, this.dtPageCount-1)
          this.dtCurrentPage = Math.max(this.dtCurrentPage, 0)

          this.recvDraw = response.data.draw
          this.currentDraw = Math.max(this.currentDraw, response.data.draw)

          this.$forceUpdate()
        }
      })
      .catch(err => {
        let errorInfo = this.processFormResponse(err)

        this.loadingError = errorInfo.errorMessage
      })
      .finally(() => {
        this.loadingData = false
      })
    },
    generateDataURL(options = {}) {
      var filterParams = ''

      // Update & check order info
      if(this.orderColumnIndex < 0 || this.orderColumnIndex >= this.$props.columns.length)
        this.resetOrderInfo()
      if(this.columns[this.orderColumnIndex].sortable !== undefined && this.columns[this.orderColumnIndex].sortable == false)
        this.resetOrderInfo()

      // Combine custom filters & location filters together
      // Assumes custom filters does not contain the location filter field, otherwise it will be duplicated
      var fullFilterList = _.cloneDeep(this.$data.filterList || [])
      fullFilterList = fullFilterList.concat(this.locationFilterList || [])
      var fullSelectedFilters = _.cloneDeep(this.$data.selectedFilters || [])
      fullSelectedFilters = fullSelectedFilters.concat(this.selectedLocationFilters || [])

      if(fullFilterList) {
        fullFilterList.forEach(filterOption => {
          if(filterOption.field == null) {
            // Treat options as the individual fields
            filterParams += _.compact(filterOption.options.map(option => {
              if(fullSelectedFilters.find(selected => selected.field == filterOption.field && selected.value == option.value) != null)
                return `&filter[${option.value}]=1`
              return null
            })).join('')

          } else {
            var filterList = _.compact(filterOption.options.map(option => {
              if(fullSelectedFilters.find(selected => selected.field == filterOption.field && selected.value == option.value) != null) {
                return encodeURIComponent(option.value)
              }
              return null
            })).join(',')

            if(filterList.length > 0) {
              let filterOp = ''
              if(filterOption.operator)
                filterOp = `[${filterOption.operator}]`
              filterParams += `&filter[${filterOption.field}]${filterOp}=${filterList}`

              // If additional filters are set, include those
              if(_.isObject(filterOption.additionalFilters)) {
                Object.keys(filterOption.additionalFilters).forEach(filterKey => {
                  filterParams += `&filter[${filterKey}]${filterOp}=${encodeURIComponent(filterOption.additionalFilters[filterKey])}`
                })
              }
            }
          }
        })
      }

      // Build up pagination data
      var length = this.dtPageLength

      var dataTableParams = {
        'search[value]': this.searchText,
        'search[regex]': false,
        format: options.format || 'datatable',
        title: options.title
      }
      if(!options.ignorePagination) {
        dataTableParams.start = this.dtStartIndex
        dataTableParams.length = this.dtPageLength
      }

      if(this.hasDateRangeFilter) {
        try {
          var dateRangeField = this.$props.dateRangeField

          // If there are multiple date columns to filter by, use that
          if(this.dateFilterColumnList.length > 0) {
            dateRangeField = this.dateFilterColumnList[this.appliedDateRangeFilter.dateFilterIndex].data
          }

          if(this.appliedDateRangeFilter.startDate != '' && this.appliedDateRangeFilter.startDate != null)
            filterParams += `&filter[${dateRangeField}][gte]=` + encodeURIComponent(moment(this.appliedDateRangeFilter.startDate).local().startOf('day').toDate().toISOString())
          if(this.appliedDateRangeFilter.endDate != '' && this.appliedDateRangeFilter.endDate != null)
            filterParams += `&filter[${dateRangeField}][lt]=` + encodeURIComponent(moment(this.appliedDateRangeFilter.endDate).local().endOf('day').toDate().toISOString())
        } catch(err) {
          console.error(err)
        }
      }

      // Column ordering & definitions
      // Note: We're only supporting simple single column ordering
      // Definitions used for export datatable features
      this.$props.columns.forEach((column, index) => {
        dataTableParams[`columns[${index}][data]`] = column.data
        dataTableParams[`columns[${index}][title]`] = column.title
        if(column.type)
          dataTableParams[`columns[${index}][type]`] = column.type
        if(column.totalField)
          dataTableParams[`columns[${index}][totalField]`] = column.totalField
        if(column.export !== undefined && !column.export)
          dataTableParams[`columns[${index}][export]`] = 0
      })
      dataTableParams['order[0][column]'] = this.orderColumnIndex
      dataTableParams['order[0][dir]'] = this.orderColumnDir

      return this.$props.url + '&' + this.buildQueryParams(dataTableParams) + filterParams
    },
    exportData(fileType) {
      this.displayProcessingDialog({
        title: 'Exporting data',
        message: 'Generating data',
        icon: 'fa-file-export',
        onBeforeOpen: (instance) => {

          var url = this.generateDataURL({ format: fileType, title: this.$props.exportTitle || '', ignorePagination: true })
          if(!url.startsWith('/'))
            url = '/' + url

          this.$api.post(`/system/signedurl`, { path: `api/v1${url}` })
          .then(response => {
            window.location.href = response.data.url
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error Exporting', 'Could not export the data')
          })
          .finally(() => {
            instance.hide()
          })
        }
      })
    },
    getRowListData() {
      return this.rowList
    },
    downloadRowData(options = {}) {
      return new Promise(async (resolve, reject) => { 
        var apiURL = this.useReportingServer ? this.$glass.reportingApiUrl : this.$glass.apiUrl
        var url = apiURL + this.generateDataURL({ ignorePagination: !!options.ignorePagination })

        return this.$api.get(url)
        .then(response => {
          resolve(response.data.data)
        })
        .catch(err => {
          let errorInfo = this.processFormResponse(err)

          reject(errorInfo.errorMessage || 'Error downloading data')
        })
      })
    }
  }
  
}
</script>